import { Badge } from '@amore/ui/components/badge'
import { Button } from '@amore/ui/components/button'
import { Field } from '@amore/ui/components/field'
import { FileInput } from '@amore/ui/components/file-input'
import { FormGrid } from '@amore/ui/components/form-layout'
import { Select, SelectItem } from '@amore/ui/components/select'
import { Textarea } from '@amore/ui/components/textarea'
import { TimePicker } from '@amore/ui/components/time-picker'
import { Text } from '@amore/ui/components/typography'
import { Link } from '@tanstack/react-router'
import { Download, FileText, Trash2, Upload } from 'lucide-react'
import { useState } from 'react'
import { newId } from '@/lib/format'
import { supplierStatusLabels, supplierStatusTone } from '@/lib/labels'
import {
  newCrewMember,
  photoRoleCategories,
  photoRoleLabels,
  removeCrewMember,
  saveCrewMember,
} from '@/mock/photo'
import { useCanEdit, useUpdate, useWedding } from '@/mock/store'
import type { PhotoCrewMember, PhotoRole } from '@/mock/types'
import { ConfirmDialog, DetailSheet } from '@/patterns/dialog'
import { LinkedRecord } from '@/patterns/linked-record'
import { useToast } from '@/shell/toaster'
import { durationLabel } from '../schedule/model'

const softControl = 'h-10 border-transparent bg-muted/60 hover:bg-muted'
const none = 'none'
const roles = Object.keys(photoRoleLabels) as PhotoRole[]

const timeLabels = {
  open: 'Vybrat čas',
  title: 'Vybrat čas',
  close: 'Zavřít výběr času',
  hours: 'Hodiny',
  minutes: 'Minuty',
  clear: 'Bez času',
  confirm: 'Potvrdit čas',
  format: '24hodinový formát · HH:mm',
}

/**
 * Who photographs or films and when: the role, the supplier from My suppliers, the hours on the
 * wedding day and a note and the contract and other files. Adding opens
 * it empty with the role preset. Money stays in Rozpočet, contacts in Dodavatelé.
 */
export function CrewSheet({
  crewId,
  role: presetRole,
  onClose,
}: {
  /** The member to edit; `null` adds a new one. */
  crewId: string | null
  role: PhotoRole
  onClose: () => void
}) {
  const data = useWedding()
  const canEdit = useCanEdit()
  const update = useUpdate()
  const { showToast } = useToast()
  const saved = data.photo.crew.find((member) => member.id === crewId)
  const [initial] = useState(() => saved ?? newCrewMember(newId('crew'), presetRole))
  const [draft, setDraft] = useState<PhotoCrewMember>(initial)
  const [confirmRemove, setConfirmRemove] = useState(false)
  const dirty = JSON.stringify(draft) !== JSON.stringify(initial)
  const supplier = data.suppliers.find((item) => item.id === draft.supplierId)
  const candidates = data.suppliers.filter(
    (item) =>
      item.id === draft.supplierId ||
      (photoRoleCategories[draft.role].includes(item.category) && item.status !== 'cancelled'),
  )
  const duration = durationLabel(draft.arrival, draft.end)
  const set = (patch: Partial<PhotoCrewMember>) => setDraft((current) => ({ ...current, ...patch }))
  const label = supplier?.name ?? photoRoleLabels[draft.role]

  const save = () => {
    update((current) => saveCrewMember(current, draft, newId('photo-arrival')))
    showToast({
      title: saved ? 'Změny uloženy' : 'Přidáno k focení',
      description: label,
      tone: 'success',
    })
    onClose()
  }

  return (
    <>
      <DetailSheet
        open
        eyebrow="Foto a video"
        title={saved ? label : `Přidat: ${photoRoleLabels[draft.role]}`}
        dirty={dirty}
        onOpenChange={(open) => {
          if (!open) onClose()
        }}
        primaryAction={canEdit ? { label: saved ? 'Uložit' : 'Přidat', onClick: save } : undefined}
        destructiveAction={
          canEdit && saved
            ? {
                label: 'Odebrat z foto a videa',
                icon: <Trash2 className="size-4" aria-hidden="true" />,
                onClick: () => setConfirmRemove(true),
              }
            : undefined
        }
      >
        <fieldset
          disabled={!canEdit}
          className="divide-y divide-border [&>*]:py-7 [&>*:first-child]:pt-0 [&>*:last-child]:pb-0"
        >
          <legend className="sr-only">Kdo a kdy</legend>
          <div className="space-y-5">
            <FormGrid className="grid-cols-2 items-start gap-x-6">
              <Field label="Co dělá" controlId="crew-role">
                <Select
                  name="crewRole"
                  variant="soft"
                  className={softControl}
                  value={draft.role}
                  onValueChange={(value) =>
                    set({ role: roles.find((role) => role === value) ?? draft.role })
                  }
                >
                  {roles.map((role) => (
                    <SelectItem key={role} value={role}>
                      {photoRoleLabels[role]}
                    </SelectItem>
                  ))}
                </Select>
              </Field>
              <Field
                label="Dodavatel"
                controlId="crew-supplier"
                description={
                  candidates.length ? 'Z Mých dodavatelů.' : 'V Mých dodavatelích zatím nikdo.'
                }
              >
                <Select
                  name="crewSupplier"
                  variant="soft"
                  className={softControl}
                  value={draft.supplierId ?? none}
                  onValueChange={(value) => set({ supplierId: value === none ? null : value })}
                >
                  <SelectItem value={none}>Zatím nevybraný</SelectItem>
                  {candidates.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name}
                    </SelectItem>
                  ))}
                </Select>
              </Field>
            </FormGrid>
            {supplier ? (
              <LinkedRecord
                module="suppliers"
                title={supplier.name}
                meta="Kontakty, smlouva a stav spolupráce"
                status={
                  <Badge tone={supplierStatusTone(supplier.status)}>
                    {supplierStatusLabels[supplier.status]}
                  </Badge>
                }
                to={`/suppliers/${supplier.id}`}
              />
            ) : canEdit ? (
              <Text variant="small" tone="muted">
                Ještě nemáte vybráno?{' '}
                <Link
                  to="/suppliers/catalog"
                  className="font-semibold text-action underline-offset-4 hover:underline"
                >
                  Najít v katalogu
                </Link>
              </Text>
            ) : null}
          </div>
          <div className="space-y-2">
            <FormGrid className="grid-cols-2 items-start gap-x-6">
              <Field label="Na svatbě od" controlId="crew-arrival">
                <TimePicker
                  name="crewArrival"
                  variant="soft"
                  className={softControl}
                  value={draft.arrival ?? ''}
                  onValueChange={(value) => set({ arrival: value || null })}
                  labels={{ ...timeLabels, open: 'Vybrat, od kdy bude na svatbě' }}
                  placeholder="Zatím nevíme"
                />
              </Field>
              <Field label="Do" controlId="crew-end">
                <TimePicker
                  name="crewEnd"
                  variant="soft"
                  className={softControl}
                  value={draft.end ?? ''}
                  onValueChange={(value) => set({ end: value || null })}
                  labels={{ ...timeLabels, open: 'Vybrat, do kdy bude na svatbě' }}
                  placeholder="Zatím nevíme"
                />
              </Field>
            </FormGrid>
            <Text variant="small" tone="muted">
              {duration ? `${duration}. ` : ''}Příjezd se objeví v harmonogramu.
            </Text>
          </div>
          <Field
            label="Poznámka"
            controlId="crew-note"
            description="Jen pro vás. Ceny a platby máte v rozpočtu."
          >
            <Textarea
              name="crewNote"
              value={draft.note}
              readOnly={!canEdit}
              rows={4}
              maxLength={3000}
              placeholder={
                canEdit
                  ? 'Balíček, jídlo pro fotografa, přejezdy, odevzdání fotek…'
                  : 'Bez poznámky'
              }
              onChange={(event) => set({ note: event.target.value })}
            />
          </Field>
        </fieldset>
        <div className="mt-7 border-t border-border pt-7">
          <CrewDocuments
            documents={draft.documents}
            canEdit={canEdit}
            onChange={(documents) => set({ documents })}
          />
        </div>
      </DetailSheet>
      <ConfirmDialog
        open={confirmRemove}
        onOpenChange={setConfirmRemove}
        heading={`Odebrat ${label} z foto a videa?`}
        description="V Mých dodavatelích zůstane. Zmizí jen odsud a jeho příjezd z harmonogramu."
        keepLabel="Ponechat"
        confirmLabel="Odebrat"
        onConfirm={() => {
          const previous = data
          update((current) => removeCrewMember(current, draft.id))
          showToast({
            title: 'Odebráno z foto a videa',
            description: label,
            action: {
              label: 'Vrátit zpět',
              altText: 'Vrátit odebraného dodavatele k focení',
              onClick: () => update(() => previous),
            },
          })
          onClose()
        }}
      />
    </>
  )
}

function CrewDocuments({
  documents,
  canEdit,
  onChange,
}: {
  documents: PhotoCrewMember['documents']
  canEdit: boolean
  onChange: (documents: PhotoCrewMember['documents']) => void
}) {
  const download = (file: File) => {
    const url = URL.createObjectURL(file)
    const link = document.createElement('a')
    link.href = url
    link.download = file.name
    link.click()
    window.setTimeout(() => URL.revokeObjectURL(url), 1000)
  }
  return (
    <section className="space-y-4">
      <div className="space-y-1">
        <h3 className="text-sm font-semibold">Podklady</h3>
        <Text variant="small" tone="muted">
          Smlouva, ceník balíčku, ukázky stylu.
        </Text>
      </div>
      {canEdit ? (
        <FileInput
          multiple
          onChange={(event) => {
            const files = Array.from(event.target.files ?? [])
            onChange([...documents, ...files.map((file) => ({ id: newId('file'), file }))])
          }}
        >
          <Upload aria-hidden="true" className="size-4" /> Přidat soubory
        </FileInput>
      ) : null}
      {documents.length ? (
        <ul className="divide-y divide-border">
          {documents.map(({ id, file }) => (
            <li key={id} className="flex items-center gap-2 py-3">
              <FileText aria-hidden="true" className="size-5 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="break-words text-sm font-medium">{file.name}</p>
                <p className="text-xs text-muted-foreground">
                  {Math.max(1, Math.round(file.size / 1024))} kB
                </p>
              </div>
              <Button
                appearance="ghost"
                tone="neutral"
                size="icon"
                aria-label={`Stáhnout ${file.name}`}
                onClick={() => download(file)}
              >
                <Download aria-hidden="true" className="size-4" />
              </Button>
              {canEdit ? (
                <Button
                  appearance="ghost"
                  tone="neutral"
                  size="icon"
                  aria-label={`Odebrat ${file.name}`}
                  onClick={() => onChange(documents.filter((doc) => doc.id !== id))}
                >
                  <Trash2 aria-hidden="true" className="size-4" />
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="py-5 text-sm text-muted-foreground">Zatím tu nejsou žádné soubory.</p>
      )}
    </section>
  )
}
